"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useIsUsiningTiers = exports.useCanUpdateTiers = exports.useIsOrgOwner = exports.useIsDsPro = exports.useIsTrial = exports.useOrganization = exports.useTiersQuery = void 0;
const react_1 = __importDefault(require("react"));
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const error_handler_1 = require("@app/util/error-handler");
const i18n_1 = require("@app/util/i18n");
const apollo_1 = require("@app/util/apollo");
const queries_1 = require("@app/queries");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const const_1 = require("@app/const");
async function tiersQuery() {
    const { data, errors } = await (0, apollo_1.query)(queries_1.TiersQueryDocument, {
        variables: {},
    });
    (0, error_handler_1.processErrors)(errors);
    if (!data?.tiers.length)
        return [];
    return data?.tiers
        .map((tier) => {
        return {
            tier: tier.tier,
            plugins: tier.plugins?.filter((plugin) => plugin.id && plugin.name && typeof plugin.tier === 'number') || [],
        };
    })
        .filter((tier) => Boolean(tier.plugins && typeof tier.tier === 'number'))
        .sort((tier1, tier2) => tier1.tier - tier2.tier);
}
function useTiersQuery({ enabled = true } = {}) {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const [isLoading, setIsLoading] = react_1.default.useState(enabled);
    const [tiers, setTiers] = react_1.default.useState(null);
    react_1.default.useEffect(() => {
        if (!enabled) {
            return;
        }
        async function fetchTiers() {
            try {
                const result = await tiersQuery();
                setTiers(result);
            }
            catch (error) {
                (0, error_handler_1.processErrors)([], (0, maybe_unwrap_1.unwrapMaybe)((0, i18n_1.t)('ERR_MESSAGE.error')));
            }
            setIsLoading(false);
        }
        void fetchTiers();
    }, [enabled]);
    const activeTierNumber = workspace?.organization?.tier;
    return {
        tiers,
        isLoading,
        activeTierNumber,
        activeTier: typeof activeTierNumber === 'number'
            ? tiers?.find((tier) => tier.tier === activeTierNumber)
            : null,
        getTierForPlugin: (pluginId) => {
            if (!tiers?.length)
                return null;
            return tiers.find((tier) => tier.plugins.some((plugin) => plugin.id === pluginId));
        },
    };
}
exports.useTiersQuery = useTiersQuery;
function useOrganization() {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const me = (0, store_1.useRootSelector)((state) => state.me);
    const organizationId = (0, store_1.useRootSelector)((state) => state.organizationId) || workspace?.organizationId;
    const organization = (0, store_1.useRootSelector)((state) => state.organization);
    const isDsProInterface = util_1.miscUtil.useIsDayswapsProInterface();
    if (organization &&
        organizationId &&
        organization.id === organizationId &&
        (isDsProInterface || workspace?.organization?.id !== organizationId)) {
        return organization;
    }
    if (organizationId && workspace?.organization?.id === organizationId) {
        return workspace?.organization;
    }
    const org = me?.organizations?.find((organization) => organization.id === organizationId);
    if (org) {
        return org;
    }
    return organization;
}
exports.useOrganization = useOrganization;
function useIsTrial() {
    const organization = useOrganization();
    return organization?.settings?.billing?.subscriptionStatus === 'trial';
}
exports.useIsTrial = useIsTrial;
function useIsDsPro() {
    const organization = useOrganization();
    return organization?.hidden === false;
}
exports.useIsDsPro = useIsDsPro;
function useIsOrgOwner() {
    const isDsPro = useIsDsPro();
    const me = (0, store_1.useRootSelector)((state) => state.me);
    const orgId = (0, store_1.useRootSelector)((state) => state.organizationId);
    const isOrgOwner = Boolean(orgId &&
        me?.acl?.organizations &&
        Object.keys(me.acl.organizations).includes(orgId) &&
        me.acl.organizations[orgId].includes('organization_owner'));
    return isDsPro && isOrgOwner;
}
exports.useIsOrgOwner = useIsOrgOwner;
function useCanUpdateTiers() {
    const { canWrite } = (0, util_1.useAccessRole)();
    const isOrgOwner = useIsOrgOwner();
    const isDsPro = useIsDsPro();
    return (isDsPro && isOrgOwner) || (!isDsPro && canWrite(const_1.PERMISSIONS.SUBSCRIPTION));
}
exports.useCanUpdateTiers = useCanUpdateTiers;
function useIsUsiningTiers() {
    const organization = useOrganization();
    return typeof organization?.tier === 'number';
}
exports.useIsUsiningTiers = useIsUsiningTiers;
